import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { Pencil, X, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { APMBadge } from '@/components/APMBadge';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface APMInstance {
  assembly_order_id: string;
  order_number: string;
  passmark_rating: number;
  unit_count: number;
}

interface APMData {
  passmark_rating: number | null;
  cpu_mark?: number | null;
  graphics_2d_mark?: number | null;
  graphics_3d_mark?: number | null;
  memory_mark?: number | null;
  disk_mark?: number | null;
  sample_count?: number | null;
  source?: 'live' | 'historical';
  instances?: APMInstance[];
}

interface APMCardProps {
  buildId: string;
  buildSku?: string;
  apmData: APMData | null;
  isAdmin: boolean;
  queryClient: QueryClient;
}

const FIELDS: { key: keyof APMData; label: string; required?: boolean }[] = [
  { key: 'passmark_rating',  label: 'Overall Score',  required: true },
  { key: 'cpu_mark',         label: 'CPU' },
  { key: 'graphics_3d_mark', label: '3D Graphics' },
  { key: 'graphics_2d_mark', label: '2D Graphics' },
  { key: 'memory_mark',      label: 'Memory' },
  { key: 'disk_mark',        label: 'Disk' },
  { key: 'sample_count',     label: 'Sample count (# tests)' },
];

export function APMCard({ buildId, buildSku, apmData, isAdmin, queryClient }: APMCardProps) {
  const isLive = apmData?.source === 'live';
  const canEdit = isAdmin && !isLive;

  const emptyForm = {
    passmark_rating: '',
    cpu_mark: '',
    graphics_3d_mark: '',
    graphics_2d_mark: '',
    memory_mark: '',
    disk_mark: '',
    sample_count: '',
  };

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, string>>(() => {
    if (!apmData) return emptyForm;
    return {
      passmark_rating:  String(apmData.passmark_rating  ?? ''),
      cpu_mark:         String(apmData.cpu_mark         ?? ''),
      graphics_3d_mark: String(apmData.graphics_3d_mark ?? ''),
      graphics_2d_mark: String(apmData.graphics_2d_mark ?? ''),
      memory_mark:      String(apmData.memory_mark      ?? ''),
      disk_mark:        String(apmData.disk_mark        ?? ''),
      sample_count:     String(apmData.sample_count     ?? ''),
    };
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, number | null> = { build_id: buildId as any };
      for (const { key } of FIELDS) {
        const raw = form[key as string];
        payload[key as string] = raw !== '' ? parseInt(raw, 10) : null;
      }
      const { error } = await supabase
        .from('build_benchmarks')
        .upsert(payload, { onConflict: 'build_id' });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['build-apm', buildId, buildSku] });
      setEditing(false);
      toast.success('APM data saved');
    },
    onError: (err: any) => toast.error('Failed to save: ' + err.message),
  });

  const handleOpen = () => {
    setForm({
      passmark_rating:  String(apmData?.passmark_rating  ?? ''),
      cpu_mark:         String(apmData?.cpu_mark         ?? ''),
      graphics_3d_mark: String(apmData?.graphics_3d_mark ?? ''),
      graphics_2d_mark: String(apmData?.graphics_2d_mark ?? ''),
      memory_mark:      String(apmData?.memory_mark      ?? ''),
      disk_mark:        String(apmData?.disk_mark        ?? ''),
      sample_count:     String(apmData?.sample_count     ?? ''),
    });
    setEditing(true);
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">AOI Performance Metric</CardTitle>
          {canEdit && !editing && (
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1" onClick={handleOpen}>
              <Pencil className="h-3 w-3" />
              {apmData?.passmark_rating != null ? 'Edit' : 'Add data'}
            </Button>
          )}
          {editing && (
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setEditing(false)}>
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!editing && apmData?.passmark_rating != null && (
          <APMBadge
            passmarkRating={apmData.passmark_rating}
            cpuMark={apmData.cpu_mark}
            graphics2dMark={apmData.graphics_2d_mark}
            graphics3dMark={apmData.graphics_3d_mark}
            memoryMark={apmData.memory_mark}
            diskMark={apmData.disk_mark}
            sampleCount={apmData.sample_count ?? undefined}
            instances={apmData.instances}
          />
        )}

        {!editing && apmData?.passmark_rating == null && canEdit && (
          <p className="text-sm text-muted-foreground">No benchmark data yet.</p>
        )}

        {editing && (
          <div className="space-y-3 pt-1">
            <div className="grid grid-cols-2 gap-3">
              {FIELDS.map(({ key, label, required }) => (
                <div key={key as string} className={key === 'passmark_rating' ? 'col-span-2' : ''}>
                  <label className="text-xs text-muted-foreground mb-1 block">
                    {label}{required && <span className="text-red-500 ml-0.5">*</span>}
                  </label>
                  <Input
                    type="number"
                    placeholder="—"
                    value={form[key as string]}
                    onChange={e => setForm(p => ({ ...p, [key as string]: e.target.value }))}
                    className="h-8 text-sm"
                  />
                </div>
              ))}
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!form.passmark_rating || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              <Save className="h-3.5 w-3.5" />
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
